/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Source Registration Admin Page
 * - Seed URL input for manual addition
 * - Filter by status + config type
 * - Bulk register for discovered URLs
 * - Run history
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '@/hooks/useUser';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  RefreshCw,
  Search,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Globe,
  ExternalLink,
  Plus,
  XCircle,
} from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';
import { AdminPageShell } from '../_components/AdminPageShell';

interface Candidate {
  id: string;
  url: string;
  city: string;
  state: string;
  status: string;
  config_type?: string;
  source_id?: string;
  auto_detect_result?: Record<string, unknown>;
  registered_at?: string;
  discovered_at: string;
}
interface RunRecord {
  id: string;
  status: string;
  started_at: string;
  finished_at?: string;
  stats: Record<string, unknown>;
}

function statusBadge(status: string) {
  const m: Record<
    string,
    {
      l: string;
      v: 'default' | 'secondary' | 'destructive' | 'outline';
      c?: string;
    }
  > = {
    registered: {
      l: 'Registered',
      v: 'outline',
      c: 'border-green-300 text-green-700 bg-green-50',
    },
    needs_jurisdiction: {
      l: 'Needs Juris.',
      v: 'outline',
      c: 'border-amber-300 text-amber-700 bg-amber-50',
    },
    discovered: { l: 'Discovered', v: 'secondary' },
    failed: { l: 'Failed', v: 'destructive' },
    duplicate: { l: 'Duplicate', v: 'secondary' },
    duplicate_candidate: { l: 'Duplicate', v: 'secondary' },
  };
  const cfg = m[status] ?? { l: status, v: 'secondary' as const };
  return (
    <Badge variant={cfg.v} className={cfg.c}>
      {cfg.l}
    </Badge>
  );
}

export const SourceRegistrationPage: React.FC = () => {
  const { user } = useUser();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [runs, setRuns] = useState<RunRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [configFilter, setConfigFilter] = useState('all');
  const [seedUrl, setSeedUrl] = useState('');
  const [seedCity, setSeedCity] = useState('');
  const [seedState, setSeedState] = useState('');
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedError, setSeedError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const fetchData = useCallback(async () => {
    try {
      const [candRes, runRes] = await Promise.all([
        apiFetch('/api/admin/source-registration/candidates?limit=500'),
        apiFetch('/api/admin/source-registration/runs?limit=20'),
      ]);
      if (candRes.ok) {
        const j = await candRes.json();
        setCandidates(j.data ?? []);
      }
      if (runRes.ok) {
        const j = await runRes.json();
        setRuns(j.data ?? []);
      }
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const configTypes = [
    ...new Set(candidates.map((c) => c.config_type).filter(Boolean)),
  ] as string[];

  const filtered = candidates.filter((c) => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (configFilter !== 'all' && c.config_type !== configFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.url.toLowerCase().includes(q) ||
      (c.city || '').toLowerCase().includes(q) ||
      (c.state || '').toLowerCase().includes(q)
    );
  });

  // Counts per status for tab badges
  const countByStatus: Record<string, number> = {};
  for (const c of candidates) {
    countByStatus[c.status] = (countByStatus[c.status] ?? 0) + 1;
  }
  const statusCounts: Record<string, number> = {
    all: candidates.length,
    registered: countByStatus.registered ?? 0,
    needs_jurisdiction: countByStatus.needs_jurisdiction ?? 0,
    failed: countByStatus.failed ?? 0,
  };

  const stats = {
    total: candidates.length,
    registered: countByStatus.registered ?? 0,
    needsJur: countByStatus.needs_jurisdiction ?? 0,
    failed: countByStatus.failed ?? 0,
  };

  async function handleSeedUrl() {
    if (!seedUrl.trim()) return;
    setSeedLoading(true);
    setSeedError(null);
    try {
      const res = await apiFetch('/api/admin/source-registration/register', {
        method: 'POST',
        body: JSON.stringify({
          candidates: [
            {
              url: seedUrl.trim(),
              city: seedCity.trim() || 'Unknown',
              state: (seedState.trim() || 'XX').toUpperCase(),
              type: 'seed',
            },
          ],
        }),
      });
      if (!res.ok)
        throw new Error(
          ((await res.json().catch(() => ({}))) as any).error ?? 'Failed'
        );
      setSeedUrl('');
      setSeedCity('');
      setSeedState('');
      void fetchData();
    } catch (e: any) {
      setSeedError(e.message);
    } finally {
      setSeedLoading(false);
    }
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  if (!user || user.role !== 'SYSTEM_ADMIN') {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        SYSTEM_ADMIN access required
      </div>
    );
  }

  return (
    <AdminPageShell title="Source Registration" sysAdminOnly>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          Auto-discovered and seeded URLs ready for registration
        </p>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Seed URL bar */}
      <Card className="bg-white border-blue-200">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-blue-700 shrink-0">
              + Add URL
            </span>
            <Input
              placeholder="https://..."
              value={seedUrl}
              onChange={(e) => setSeedUrl(e.target.value)}
              className="flex-1 min-w-[200px] bg-white"
            />
            <Input
              placeholder="City"
              value={seedCity}
              onChange={(e) => setSeedCity(e.target.value)}
              className="w-28 bg-white"
            />
            <Input
              placeholder="ST"
              value={seedState}
              onChange={(e) => setSeedState(e.target.value)}
              className="w-16 bg-white"
              maxLength={2}
            />
            <Button
              size="sm"
              onClick={handleSeedUrl}
              disabled={seedLoading || !seedUrl.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {seedLoading ? (
                'Adding…'
              ) : (
                <>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add
                </>
              )}
            </Button>
            {seedError && (
              <span className="text-red-500 text-xs flex items-center gap-1">
                <XCircle className="w-3 h-3" />
                {seedError}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500">Total</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
              <p className="text-xs text-gray-500">Registered</p>
            </div>
            <p className="text-2xl font-bold text-green-700">
              {stats.registered}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
              <p className="text-xs text-gray-500">Needs Juris.</p>
            </div>
            <p className="text-2xl font-bold text-amber-700">
              {stats.needsJur}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white border-red-200">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500">Failed</p>
            <p className="text-2xl font-bold text-red-700">{stats.failed}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by URL, city, or state…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white"
          />
        </div>
        <div className="flex gap-1">
          {['all', 'registered', 'needs_jurisdiction', 'failed'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md ${statusFilter === s ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {s === 'all'
                ? 'All'
                : s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              <span className="ml-1 opacity-60">({statusCounts[s] ?? 0})</span>
            </button>
          ))}
        </div>
        {configTypes.length > 0 && (
          <select
            value={configFilter}
            onChange={(e) => setConfigFilter(e.target.value)}
            className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white"
          >
            <option value="all">All Types</option>
            {configTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Candidates table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm max-h-[55vh] overflow-y-auto">
        <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <span className="text-xs text-gray-500">
            Showing{' '}
            <span className="font-medium text-gray-700">{filtered.length}</span>{' '}
            of{' '}
            <span className="font-medium text-gray-700">
              {candidates.length}
            </span>{' '}
            candidates
          </span>
        </div>
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-gray-50">
            <TableRow>
              <TableHead className="text-xs font-bold w-8"></TableHead>
              <TableHead className="text-xs font-bold">URL</TableHead>
              <TableHead className="text-xs font-bold">City</TableHead>
              <TableHead className="text-xs font-bold">State</TableHead>
              <TableHead className="text-xs font-bold w-36">Status</TableHead>
              <TableHead className="text-xs font-bold">Discovered</TableHead>
              <TableHead className="text-xs font-bold text-right">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-gray-500"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-gray-500"
                >
                  No candidates found
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((c) => (
                <TableRow key={c.id} className="border-b border-gray-100">
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      onChange={() => toggleSelect(c.id)}
                      className="rounded"
                    />
                  </TableCell>
                  <TableCell className="text-sm max-w-xs truncate">
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener"
                      className="text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <Globe className="w-3 h-3" />
                      <span className="truncate">{c.url}</span>
                    </a>
                  </TableCell>
                  <TableCell className="text-sm text-gray-700">
                    {c.city || '—'}
                  </TableCell>
                  <TableCell className="text-sm text-gray-700">
                    {c.state || '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      {statusBadge(c.status)}
                      {(c.city?.toLowerCase().includes('county') ||
                        c.url?.toLowerCase().includes('county')) && (
                        <Badge
                          variant="outline"
                          className="border-purple-300 bg-purple-50 text-purple-700 text-[10px] px-1.5"
                        >
                          County
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-gray-500">
                    {c.discovered_at
                      ? new Date(c.discovered_at).toLocaleDateString()
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    {c.source_id ? (
                      <a
                        href={`/sources?q=${encodeURIComponent(c.city)}`}
                        target="_blank"
                        rel="noopener"
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" /> View
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Run history */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4" /> Run History
        </h3>
        {runs.length === 0 ? (
          <p className="text-sm text-gray-500">
            No runs recorded yet. Runs are created when a checker POSTs
            candidates to the registration API.
          </p>
        ) : (
          <div className="max-h-[30vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Checker</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Started</TableHead>
                  <TableHead className="text-xs">Duration</TableHead>
                  <TableHead className="text-xs text-right">Total</TableHead>
                  <TableHead className="text-xs text-right">
                    Registered
                  </TableHead>
                  <TableHead className="text-xs text-right">
                    Duplicates
                  </TableHead>
                  <TableHead className="text-xs text-right">Failed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs font-medium text-gray-700">
                      {(() => {
                        const ct = r.stats?.config_type as string | undefined;
                        if (!ct) return '—';
                        if (ct === 'civicweb') return 'CivicWeb';
                        if (ct === 'municode') return 'Municode';
                        if (ct === 'seed') return 'Manual Seed';
                        return ct;
                      })()}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          r.status === 'completed' ? 'outline' : 'secondary'
                        }
                        className={
                          r.status === 'completed'
                            ? 'border-green-300 text-green-700'
                            : ''
                        }
                      >
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {r.started_at
                        ? new Date(r.started_at).toLocaleString()
                        : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {r.started_at && r.finished_at
                        ? `${Math.round((new Date(r.finished_at).getTime() - new Date(r.started_at).getTime()) / 1000)}s`
                        : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-right font-medium">
                      {String(r.stats?.total ?? 0)}
                    </TableCell>
                    <TableCell className="text-xs text-right text-green-700">
                      {String(r.stats?.registered ?? 0)}
                    </TableCell>
                    <TableCell className="text-xs text-right text-gray-500">
                      {String(r.stats?.duplicates ?? 0)}
                    </TableCell>
                    <TableCell className="text-xs text-right text-red-600">
                      {String(r.stats?.failed ?? 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </AdminPageShell>
  );
};

export default SourceRegistrationPage;
